import { forwardRef, createElement } from 'react';

/**
 * Factory function to create a basic component that doesn't support refs.
 *
 * @param component - The component render function.
 */ const createComponent = (render)=>render;
/**
 * Factory function to create a component with a ref. This is a simple wrapper around React's
 * `forwardRef` function, but provides a similar API to create both regular and polymorphic
 * components.
 *
 * @param component - The component render function.
 */ const createComponentWithRef = (component)=>forwardRef(component);
/**
 * Factory function to create a sub-component for a compound component.
 *
 * @param component - The component render function.
 * @param name - The name of the component.
 */ const createSubcomponent = (component, name)=>{
    const subcomponent = createComponentWithRef((props, ref)=>createElement(component, {
            ...props,
            ref
        }));
    subcomponent.displayName = name;
    subcomponent.getCollectionNode = component.getCollectionNode;
    return subcomponent;
};
/**
 * Factory function to create a compound component with the correct display names for the root and
 * subcomponents.
 *
 * @param name - The name of the component
 * @param component - The component render function
 * @param subcomponents - Map of subcomponent names to subcomponent render functions
 */ const compound = (name, component, subcomponents)=>{
    component.displayName = name;
    const subcomponentsMapped = Object.entries(subcomponents).reduce((accumulator, [subcomponentName, subcomponent])=>({
            ...accumulator,
            [subcomponentName]: createSubcomponent(subcomponent, `${name}.${subcomponentName}`)
        }), {});
    return Object.assign(component, subcomponentsMapped);
};
/**
 * Factory function to create a polymorphic component using the "as" prop.
 *
 * @param component - The component render function.
 */ const createPolymorphicComponent = forwardRef;

export { compound, createComponent, createComponentWithRef, createPolymorphicComponent, createSubcomponent };
//# sourceMappingURL=factories.js.map
